/**
 * Centralized Theme Configuration for Datawyse AI
 * All colors and fonts are defined here for easy theme switching
 */

// Color Palette - Based on your specifications
export const colorPalette = {
  // Alice Blue variations (light backgrounds)
  alice_blue: {
    DEFAULT: '#dbeafe',
    100: '#02295c',
    200: '#0553b9',
    300: '#2380f9',
    400: '#80b5fc',
    500: '#dbeafe',
    600: '#e3effe',
    700: '#eaf3fe',
    800: '#f1f7ff',
    900: '#f8fbff',
  },

  // Jordy Blue variations
  jordy_blue: {
    DEFAULT: '#93c5fd',
    100: '#02254e',
    200: '#034a9c',
    300: '#0570ea',
    400: '#4399fb',
    500: '#93c5fd',
    600: '#a7cffd',
    700: '#bddbfe',
    800: '#d3e7fe',
    900: '#e9f3ff',
  },

  // Royal Blue (Primary) - #2563eb
  royal_blue: {
    DEFAULT: '#2563eb',
    100: '#051331',
    200: '#092663',
    300: '#0e3894',
    400: '#124bc6',
    500: '#2563eb',
    600: '#5082ef',
    700: '#7ba1f3',
    800: '#a7c0f7',
    900: '#d3e0fb',
  },

  // Marian Blue (Secondary) - #1e3a8a
  marian_blue: {
    DEFAULT: '#1e3a8a',
    100: '#060c1c',
    200: '#0c1837',
    300: '#122353',
    400: '#182f6e',
    500: '#1e3a8a',
    600: '#2b53c2',
    700: '#577ada',
    800: '#8fa6e6',
    900: '#c7d3f3',
  },

  // Space Cadet (Borders) - #172554
  space_cadet: {
    DEFAULT: '#172554',
    100: '#050711',
    200: '#090f22',
    300: '#0e1632',
    400: '#121e43',
    500: '#172554',
    600: '#284193',
    700: '#4161cb',
    800: '#8096dc',
    900: '#c0caee',
  },
} as const;

// Semantic Color Mapping
export const themeColors = {
  // Primary colors - for buttons, CTAs, major non-text elements
  primary: {
    DEFAULT: colorPalette.royal_blue.DEFAULT, // #2563eb
    light: colorPalette.royal_blue[800], // lighter shade
    lighter: colorPalette.royal_blue[900], // lightest shade
    dark: colorPalette.royal_blue[400], // darker shade
    darker: colorPalette.royal_blue[300], // darkest shade
    50: colorPalette.royal_blue[900],
    100: colorPalette.royal_blue[800],
    200: colorPalette.royal_blue[700],
    300: colorPalette.royal_blue[600],
    400: colorPalette.royal_blue[500],
    500: colorPalette.royal_blue.DEFAULT,
    600: colorPalette.royal_blue[400],
    700: colorPalette.royal_blue[300],
    800: colorPalette.royal_blue[200],
    900: colorPalette.royal_blue[100],
  },

  // Secondary colors - for highlights, text attention
  secondary: {
    DEFAULT: colorPalette.marian_blue.DEFAULT, // #1E3A8A
    light: colorPalette.marian_blue[800],
    lighter: colorPalette.marian_blue[900],
    dark: colorPalette.marian_blue[400],
    darker: colorPalette.marian_blue[300],
    50: colorPalette.marian_blue[900],
    100: colorPalette.marian_blue[800],
    200: colorPalette.marian_blue[700],
    300: colorPalette.marian_blue[600],
    400: colorPalette.marian_blue[500],
    500: colorPalette.marian_blue.DEFAULT,
    600: colorPalette.marian_blue[400],
    700: colorPalette.marian_blue[300],
    800: colorPalette.marian_blue[200],
    900: colorPalette.marian_blue[100],
  },

  // Tertiary colors - for borders
  tertiary: {
    DEFAULT: colorPalette.space_cadet.DEFAULT, // #172554
    light: colorPalette.space_cadet[800],
    lighter: colorPalette.space_cadet[900],
    dark: colorPalette.space_cadet[400],
    darker: colorPalette.space_cadet[300],
    50: colorPalette.space_cadet[900],
    100: colorPalette.space_cadet[800],
    200: colorPalette.space_cadet[700],
    300: colorPalette.space_cadet[600],
    400: colorPalette.space_cadet[500],
    500: colorPalette.space_cadet.DEFAULT,
    600: colorPalette.space_cadet[400],
    700: colorPalette.space_cadet[300],
    800: colorPalette.space_cadet[200],
    900: colorPalette.space_cadet[100],
  },

  // Quaternary colors - for light backgrounds
  quaternary: {
    DEFAULT: colorPalette.alice_blue.DEFAULT, // #dbeafe
    light: colorPalette.alice_blue[700],
    lighter: colorPalette.alice_blue[800],
    lightest: colorPalette.alice_blue[900],
    dark: colorPalette.alice_blue[400],
    darker: colorPalette.alice_blue[300],
    50: colorPalette.alice_blue[900],
    100: colorPalette.alice_blue[800],
    200: colorPalette.alice_blue[700],
    300: colorPalette.alice_blue[600],
    400: colorPalette.alice_blue[500],
    500: colorPalette.alice_blue.DEFAULT,
    600: colorPalette.alice_blue[400],
    700: colorPalette.alice_blue[300],
    800: colorPalette.alice_blue[200],
    900: colorPalette.alice_blue[100],
  },

  // Neutral colors (keeping existing good ones)
  neutral: {
    white: '#ffffff',
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },
} as const;

// Font Configuration
export const fontConfig = {
  primary: {
    name: 'Poppins',
    fallback: ['system-ui', 'sans-serif'],
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
  },
} as const;

// Theme Configuration
export const theme = {
  colors: themeColors,
  fonts: fontConfig,
  background: {
    primary: themeColors.neutral.white, // White background for light theme
    secondary: themeColors.quaternary[100], // Very light blue for sections
    accent: themeColors.quaternary.DEFAULT, // Light blue for cards/highlights
  },
  text: {
    primary: themeColors.neutral[900], // Dark text on light background
    secondary: themeColors.neutral[600], // Medium gray for descriptions
    accent: themeColors.secondary.DEFAULT, // Secondary color for highlights
    inverse: themeColors.neutral.white, // White text on dark backgrounds
  },
} as const;

// Utility functions
export const getColor = (path: string): string => {
  const keys = path.split('.');
  let current: any = theme.colors;

  for (const key of keys) {
    if (current[key]) {
      current = current[key];
    } else {
      console.warn(`Color path '${path}' not found in theme`);
      return theme.colors.primary.DEFAULT;
    }
  }

  return typeof current === 'string'
    ? current
    : current.DEFAULT || current[500] || '';
};

export const getFont = (
  fontName: keyof typeof fontConfig = 'primary'
): string => {
  const font = fontConfig[fontName];
  return `"${font.name}", ${font.fallback.join(', ')}`;
};

// CSS Custom Properties Generator (for runtime theme switching)
export const generateCSSCustomProperties = () => {
  const properties: Record<string, string> = {};

  // Generate color properties
  Object.entries(themeColors).forEach(([colorName, colorValues]) => {
    if (typeof colorValues === 'object') {
      Object.entries(colorValues).forEach(([shade, value]) => {
        if (typeof value === 'string') {
          properties[`--color-${colorName}-${shade}`] = value;
        }
      });
    }
  });

  // Font properties
  properties['--font-primary'] = getFont('primary');

  return properties;
};

export type ThemeColors = typeof themeColors;
export type ColorPath = keyof ThemeColors;
export default theme;
